/**
 * Workflow Converter Utility
 * Converts React Flow nodes/edges to proper backend Workflow format
 * 
 * This bridges the gap between:
 * - Frontend: React Flow nodes and edges (visual representation)
 * - Backend: states, assistants, metaStates, edges (execution format)
 */

import { Node, Edge } from 'reactflow';

// ============================================================================
// Backend Types (matching backend/src/types/workflow.ts)
// ============================================================================

export interface WorkflowAssistantRef {
  id: string;           // local reference ID within workflow
  assistantId: string;  // actual assistant ID from database
}

export interface WorkflowState {
  id: string;
  assistantId: string;  // references WorkflowAssistantRef.id
  task?: string;
  outputSchema?: string;
  next?: NextStateConfig;
}

export interface NextStateConfig {
  stateId?: string;
  iterKey?: string;
  metaIterStateId?: string;
  metaNextStateId?: string;
  condition?: {
    expression: string;
    then: string;
    otherwise: string;
  };
}

export interface WorkflowMetaState {
  id: string;
  type: string;
  isConnected: boolean;
  position: { x: number; y: number };
  measured?: { width: number; height: number };
  selected?: boolean;
  data?: Record<string, any>;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
  animated?: boolean;
  label?: string;
}

export interface ConvertedWorkflowData {
  assistants: WorkflowAssistantRef[];
  states: WorkflowState[];
  metaStates: WorkflowMetaState[];
  edges: WorkflowEdge[];
}

// ============================================================================
// Node Type Detection
// ============================================================================

const STATE_NODE_TYPES = ['assistant', 'tool', 'custom', 'transform'];
const CONTROL_NODE_TYPES = ['conditional', 'switch', 'iterator'];
const UTILITY_NODE_TYPES = ['start', 'end', 'note'];

/**
 * Check if a node is connected to the workflow
 */
const isNodeConnected = (nodeId: string, edges: Edge[]): boolean => {
  return edges.some(edge => edge.source === nodeId || edge.target === nodeId);
};

/**
 * Get the state ID from a node (handles different naming conventions)
 */
const getStateId = (node: Node): string => {
  return node.data?.stateId || node.data?.label || node.id;
};

// ============================================================================
// Assistant Extraction
// ============================================================================

/**
 * Extract unique assistants from nodes
 * Creates the assistants[] array for the backend
 */
export const extractAssistants = (nodes: Node[]): WorkflowAssistantRef[] => {
  const assistantMap = new Map<string, WorkflowAssistantRef>();
  
  nodes.forEach(node => {
    // Only assistant nodes have actual assistant references
    if (node.type === 'assistant' && node.data?.assistantId) {
      const stateId = getStateId(node);
      const assistantId = node.data.assistantId;
      
      // Use stateId as local reference, assistantId is the actual DB reference
      if (!assistantMap.has(stateId)) {
        assistantMap.set(stateId, {
          id: stateId,
          assistantId: assistantId,
        });
      }
    }
  });
  
  return Array.from(assistantMap.values());
};

// ============================================================================
// State Generation
// ============================================================================

/**
 * Find the next state for a node based on edges
 */
const findNextState = (
  node: Node,
  edges: Edge[],
  nodes: Node[]
): NextStateConfig | undefined => {
  const outgoingEdges = edges.filter(edge => edge.source === node.id);
  
  if (outgoingEdges.length === 0) {
    return undefined;
  }
  
  const nodeType = node.type || '';
  const nodeData = node.data || {};
  
  // Handle conditional nodes
  if (nodeType === 'conditional') {
    const thenEdge = outgoingEdges.find(e => 
      e.sourceHandle === 'then' || e.label === 'then' || e.data?.branch === 'then'
    );
    const otherwiseEdge = outgoingEdges.find(e => 
      e.sourceHandle === 'otherwise' || e.label === 'otherwise' || e.data?.branch === 'otherwise'
    );
    
    // Get target state IDs
    const thenNode = thenEdge ? nodes.find(n => n.id === thenEdge.target) : undefined;
    const otherwiseNode = otherwiseEdge ? nodes.find(n => n.id === otherwiseEdge.target) : undefined;
    
    return {
      condition: {
        expression: nodeData.expression || nodeData.next?.condition?.expression || '',
        then: thenNode ? getStateId(thenNode) : (nodeData.then || ''),
        otherwise: otherwiseNode ? getStateId(otherwiseNode) : (nodeData.otherwise || ''),
      },
      iterKey: nodeData.iterKey,
      metaNextStateId: getStateId(node),
    };
  }
  
  // Handle iterator nodes
  if (nodeType === 'iterator') {
    const firstChildEdge = outgoingEdges[0];
    const firstChildNode = firstChildEdge ? nodes.find(n => n.id === firstChildEdge.target) : undefined;
    
    return {
      stateId: firstChildNode ? getStateId(firstChildNode) : undefined,
      iterKey: nodeData.iterKey || nodeData.next?.iter_key || 'items',
      metaIterStateId: getStateId(node),
    };
  }
  
  // Handle switch nodes
  if (nodeType === 'switch') {
    // Switch uses condition property with cases
    const cases = (nodeData.cases || []).map((c: any, idx: number) => ({
      condition: c.condition || c.expression || 'true',
      stateId: c.stateId || outgoingEdges[idx]?.target || '',
    }));
    
    // For now, we use first edge as primary next
    // The switch logic is handled in condition evaluation
    const firstEdge = outgoingEdges[0];
    return {
      stateId: firstEdge?.target,
    };
  }
  
  // Handle regular state nodes (assistant, tool, custom, transform)
  const nextEdge = outgoingEdges[0];
  if (!nextEdge) return undefined;
  
  const nextNode = nodes.find(n => n.id === nextEdge.target);
  const nextStateId = nextNode ? getStateId(nextNode) : nextEdge.target;
  
  const result: NextStateConfig = {
    stateId: nextStateId,
  };
  
  // Add iterator context if present
  if (nodeData.iterKey) {
    result.iterKey = nodeData.iterKey;
  }
  if (nodeData.next?.metaIterStateId || nodeData.parentIteratorId) {
    const iteratorId = nodeData.parentIteratorId || nodeData.next?.metaIterStateId;
    const iteratorNode = nodes.find(n => n.id === iteratorId);
    if (iteratorNode) {
      result.metaIterStateId = getStateId(iteratorNode);
    }
  }
  if (nodeData.next?.metaNextStateId) {
    result.metaNextStateId = nodeData.next.metaNextStateId;
  }
  
  return result;
};

/**
 * Generate workflow states from nodes
 * Creates the states[] array for the backend execution engine
 */
export const generateStates = (nodes: Node[], edges: Edge[]): WorkflowState[] => {
  const states: WorkflowState[] = [];
  
  nodes.forEach(node => {
    const nodeType = node.type || '';
    
    // Only process state-producing nodes
    if (!STATE_NODE_TYPES.includes(nodeType)) return;
    
    // Only include connected nodes
    if (!isNodeConnected(node.id, edges)) return;
    
    const stateId = getStateId(node);
    const nodeData = node.data || {};
    
    // For tool nodes, the assistantId is the stateId (self-referencing)
    // For assistant nodes, use the local reference ID
    const assistantId = nodeType === 'tool' ? stateId : (nodeData.stateId || stateId);
    
    const state: WorkflowState = {
      id: stateId,
      assistantId: assistantId,
    };
    
    // Add task if present
    if (nodeData.task) {
      state.task = nodeData.task;
    }
    
    // Add output schema if present
    if (nodeData.outputSchema) {
      state.outputSchema = typeof nodeData.outputSchema === 'string' 
        ? nodeData.outputSchema 
        : JSON.stringify(nodeData.outputSchema);
    }
    
    // Calculate next state
    const nextConfig = findNextState(node, edges, nodes);
    if (nextConfig) {
      state.next = nextConfig;
    }
    
    states.push(state);
  });
  
  // Also generate states for control nodes that need state representation
  nodes.forEach(node => {
    const nodeType = node.type || '';
    
    if (!CONTROL_NODE_TYPES.includes(nodeType)) return;
    if (!isNodeConnected(node.id, edges)) return;
    
    const stateId = getStateId(node);
    const nodeData = node.data || {};
    
    // Control nodes also become states in the execution flow
    const state: WorkflowState = {
      id: stateId,
      assistantId: stateId, // Control nodes are self-referencing
    };
    
    // Calculate next state with control flow logic
    const nextConfig = findNextState(node, edges, nodes);
    if (nextConfig) {
      state.next = nextConfig;
    }
    
    states.push(state);
  });
  
  return states;
};

// ============================================================================
// Meta State Generation
// ============================================================================

/**
 * Generate meta states for visual representation
 * Creates the metaStates[] array for the backend (canvas positioning)
 */
export const generateMetaStates = (nodes: Node[], edges: Edge[]): WorkflowMetaState[] => {
  return nodes.map(node => {
    const nodeType = node.type || 'unknown';
    const nodeData = node.data || {};
    
    // Get measured dimensions
    const measured = (node as any).measured;
    let width = 256;
    let height = 67;
    
    if (nodeType === 'iterator') {
      width = nodeData.width || measured?.width || 300;
      height = nodeData.height || measured?.height || 200;
    } else if (measured) {
      width = typeof measured.width === 'number' ? measured.width : 256;
      height = typeof measured.height === 'number' ? measured.height : 67;
    }
    
    const metaState: WorkflowMetaState = {
      id: node.id,
      type: nodeType,
      isConnected: isNodeConnected(node.id, edges),
      position: {
        x: node.position.x,
        y: node.position.y,
      },
      measured: { width, height },
      selected: node.selected || false,
    };
    
    // Add node-specific data
    if (CONTROL_NODE_TYPES.includes(nodeType)) {
      metaState.data = buildControlNodeData(node);
    } else if (STATE_NODE_TYPES.includes(nodeType)) {
      metaState.data = buildStateNodeData(node);
    } else {
      metaState.data = {};
    }
    
    // Add parent iterator reference
    if (nodeData.parentIteratorId) {
      (metaState.data as any).parentIteratorId = nodeData.parentIteratorId;
    }
    
    return metaState;
  });
};

/**
 * Build data object for control nodes
 */
const buildControlNodeData = (node: Node): Record<string, any> => {
  const nodeType = node.type || '';
  const nodeData = node.data || {};
  
  const data: Record<string, any> = {};
  
  if (nodeType === 'iterator') {
    data.iterKey = nodeData.iterKey || 'items';
    data.childNodeIds = nodeData.childNodeIds || [];
    data.next = {
      iter_key: nodeData.iterKey || 'items',
    };
  } else if (nodeType === 'conditional') {
    data.expression = nodeData.expression || '';
    data.then = nodeData.then || '';
    data.otherwise = nodeData.otherwise || '';
    data.next = {
      condition: {
        expression: nodeData.expression || '',
        then: nodeData.then || '',
        otherwise: nodeData.otherwise || '',
      },
    };
  } else if (nodeType === 'switch') {
    data.cases = nodeData.cases || [];
    data.default = nodeData.default || '';
    data.next = {
      switch: {
        cases: (nodeData.cases || []).map((c: any) => ({
          condition: c.condition || '',
          state_id: c.stateId || '',
        })),
        default: nodeData.default || '',
      },
    };
  }
  
  return data;
};

/**
 * Build data object for state nodes
 */
const buildStateNodeData = (node: Node): Record<string, any> => {
  const nodeType = node.type || '';
  const nodeData = node.data || {};
  
  const data: Record<string, any> = {
    label: nodeData.label || getStateId(node),
    stateId: getStateId(node),
  };
  
  if (nodeType === 'assistant') {
    data.assistantId = nodeData.assistantId;
    data.task = nodeData.task;
    data.outputSchema = nodeData.outputSchema;
    data.useVirtualAssistant = nodeData.useVirtualAssistant ?? true;
    data.keepHistory = nodeData.keepHistory ?? true;
    data.storeInContext = nodeData.storeInContext ?? true;
    data.includeInLlmHistory = nodeData.includeInLlmHistory ?? true;
  } else if (nodeType === 'tool') {
    data.toolId = nodeData.toolId || nodeData.selectedTool;
    data.selectedTool = nodeData.selectedTool;
    data.task = nodeData.task;
    data.storeInContext = nodeData.storeInContext ?? true;
  } else {
    // custom, transform
    data.task = nodeData.task;
    data.outputSchema = nodeData.outputSchema;
  }
  
  return data;
};

// ============================================================================
// Edge Conversion
// ============================================================================

/**
 * Convert React Flow edges to workflow edges
 */
export const convertEdges = (edges: Edge[]): WorkflowEdge[] => {
  return edges.map(edge => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || undefined,
    targetHandle: edge.targetHandle || undefined,
    type: edge.type || 'default',
    animated: edge.animated || false,
    label: typeof edge.label === 'string' ? edge.label : undefined,
  }));
};

// ============================================================================
// Main Conversion Function
// ============================================================================

/**
 * Convert React Flow nodes and edges to backend workflow format
 * 
 * This is the main function that should be called when saving a workflow.
 * It produces all four arrays needed by the backend:
 * - assistants: References to AI assistants used
 * - states: Execution states with task definitions and transitions
 * - metaStates: Visual representation for the canvas
 * - edges: Connections between nodes
 * 
 * @param nodes - React Flow nodes from the canvas
 * @param edges - React Flow edges connecting nodes
 * @returns ConvertedWorkflowData ready for backend API
 */
export const convertNodesToWorkflow = (
  nodes: Node[],
  edges: Edge[]
): ConvertedWorkflowData => {
  return {
    assistants: extractAssistants(nodes),
    states: generateStates(nodes, edges),
    metaStates: generateMetaStates(nodes, edges),
    edges: convertEdges(edges),
  };
};

/**
 * Validate the converted workflow data
 * Returns any issues found that would prevent execution
 */
export const validateWorkflowData = (data: ConvertedWorkflowData): string[] => {
  const errors: string[] = [];
  
  // Check for states
  if (data.states.length === 0) {
    errors.push('Workflow has no executable states. Add at least one assistant or tool node.');
  }
  
  // Check for start node
  const hasStart = data.metaStates.some(m => m.type === 'start');
  if (!hasStart) {
    errors.push('Workflow is missing a start node.');
  }
  
  // Check for end node
  const hasEnd = data.metaStates.some(m => m.type === 'end');
  if (!hasEnd) {
    errors.push('Workflow is missing an end node.');
  }
  
  // Check that all states have valid next references
  const stateIds = new Set(data.states.map(s => s.id));
  const metaIds = new Set(data.metaStates.map(m => m.id));
  
  data.states.forEach(state => {
    if (state.next?.stateId && !stateIds.has(state.next.stateId) && !metaIds.has(state.next.stateId)) {
      errors.push(`State "${state.id}" references unknown next state "${state.next.stateId}".`);
    }
  });
  
  // Check that assistants are referenced
  const assistantIds = new Set(data.assistants.map(a => a.id));
  data.states
    .filter(s => {
      const meta = data.metaStates.find(m => m.id === s.id);
      return meta?.type === 'assistant';
    })
    .forEach(state => {
      if (!assistantIds.has(state.assistantId)) {
        errors.push(`State "${state.id}" references unknown assistant "${state.assistantId}".`);
      }
    });
  
  return errors;
};

// Export all utilities
export {
  STATE_NODE_TYPES,
  CONTROL_NODE_TYPES,
  UTILITY_NODE_TYPES,
  isNodeConnected,
  getStateId,
};
